import NotificationIcon from './NotificationIcon';
import SupportConversations from './SupportConversations';
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { createPortal } from 'react-dom';
import type { ChatDto, ChatSummary, MemberDto, UserBriefDto, UserDto } from '../api/types';
import { ApiError } from '../api/client';
import { archiveChat, createChat, deleteChat, getOrCreateNotesChat, listMembers, pinChat, removeMember, searchUsers, unarchiveChat, unpinChat } from '../api/chats';
import { addContact, listContacts, removeContact } from '../api/contacts';
import { getFolderPrefs, listFolders, type ChatFolderDto } from '../api/folders';
import { sortByFolderOrder, SMART_FOLDERS, type SmartFolderId } from '../lib/smartFolders';
import { isTechnicalDiagnosticMessage } from '../lib/technicalDiagnosticMessage';
import { isChatMutedAt } from '../lib/chatMute';
import { publishStory, publishStoryVideo, storyFeed, type StoryAuthorDto } from '../api/stories';
import { uploadMedia, uploadMediaWithProgress } from '../api/media';
// Обрезка видео для сторис — тяжёлый компонент (загружает файл на клиенте), лениво, как в ChatView.
const VideoTrimEditor = lazy(() => import('./VideoTrimEditor'));
// Запись аудио/видео сторис с камеры/микрофона — лениво, т.к. используется редко.
const StoryRecorder = lazy(() => import('./StoryRecorder'));
import { globalSearch, type SearchResultDto } from '../api/search';
import StoryViewer from './StoryViewer';
import { useConnectionStatus } from '../lib/useConnectionStatus';
import { formatPresence } from '../lib/presence';
import { showTransferNotice } from '../lib/transferPolicy';
import { compressPhotoForUpload } from '../lib/compressPhoto';
import Avatar from './Avatar';
import ConnectingBadge from './ConnectingBadge';
import NewChat from './NewChat';
import ConfirmModal from './ConfirmModal';
import OwnerLeaveModal from './OwnerLeaveModal';
import GlobalSearchResults from './GlobalSearchResults';
import BirthdayBanner from './BirthdayBanner';
import { MyContactQrModal } from './ContactQr';
import { chatDraft, draftsVersion, subscribeDrafts } from '../lib/drafts';
import { intlLocale } from '../lib/language';
import { chatArchiveReducer, initialChatArchiveState, shouldRevealArchive } from '../lib/chatArchive';

interface Props {
  chats: ChatSummary[];
  activeId: string | null;
  user: UserDto | null;
  onOpen: (id: string) => void;
  onOpenSupport: (chat: ChatDto, applicant?: UserBriefDto) => Promise<void>;
  onRefresh: () => void;
  onLogout: () => void;
  /** tab='account' — открыть настройки сразу на разделе «Аккаунт» (клик по имени в меню). */
  onOpenSettings: (tab?: 'account') => void;
  onOpenAdmin: () => void;
  /** Меняется при создании/переименовании/удалении папки или изменении её состава в
   *  настройках (см. SettingsModal, вкладка «Папки») — сигнал перечитать список папок. */
  foldersVersion: number;
  /** Контакт добавлен в другом оверлее — перечитать список контактов. */
  contactsVersion: number;
  /** Открыть чат и проскроллить к конкретному сообщению — переход из результата глобального
   *  поиска (см. GlobalSearchResults). */
  onOpenMessage: (chatId: string, messageId: number) => void;
  /** Свернуть список до одних аватарок — освобождает место под колонку комнат группового
   *  чата (см. App.tsx/RoomsColumn, отчёт пользователя "в основном чате сворачивать до
   *  аватарки чаты"). Только десктоп — на мобильном игнорируется чисто на CSS (см. .sidebar
   *  -collapsed в theme.css, медиазапрос max-width:720px), там свой полноэкранный сценарий. */
  collapsed?: boolean;
}

/** Кольцо-полоски вокруг аватарки сторис: по одному сегменту на каждую сторис автора;
 *  полупрозрачный сегмент = эта сторис уже просмотрена (флаг viewed приходит с сервера, см.
 *  api/stories.ts). Заменяет прежнее сплошное градиентное кольцо (отчёт пользователя "вокруг
 *  аватарки отобразить в виде полосок количество сторис, если полупрозрачные — просмотрено"). */
function StoryRing({ stories, children }: { stories: StoryAuthorDto['stories']; children: React.ReactNode }) {
  const n = stories.length;
  const size = 64;
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const gap = 2.5;
  const seg = n > 0 ? c / n - gap : 0;
  let off = 0;
  return (
    <span className="story-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {stories.map((s) => {
          const el = (
            <circle
              key={s.id}
              cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={s.viewed ? 'rgba(139, 92, 246, 0.3)' : 'var(--accent)'}
              strokeWidth="3.5" strokeLinecap="round"
              strokeDasharray={`${seg} ${c - seg}`} strokeDashoffset={-off}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          off += seg + gap;
          return el;
        })}
      </svg>
      {children}
    </span>
  );
}

function timeOf(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const locale = intlLocale(i18n.resolvedLanguage ?? i18n.language);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (Math.floor((startOfToday - t) / 86400000) < 7) return d.toLocaleDateString(locale, { weekday: 'short' });
  const dateOptions: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== now.getFullYear()) dateOptions.year = 'numeric';
  return d.toLocaleDateString(locale, dateOptions);
}

function previewOf(c: ChatSummary, me: UserDto | null): React.ReactNode {
  // Незаконченный черновик показываем вместо последнего сообщения — как в Telegram.
  const draft = me ? chatDraft(me.id, c.id) : '';
  if (draft) {
    return (
      <>
        <span className="chat-item-draft">{i18n.t('chats:preview.draft')}</span>
        {draft.replace(/\s+/g, ' ').slice(0, 120)}
      </>
    );
  }
  const supportOperator = me?.role === 'admin' || me?.role === 'support';
  const last = c.lastMessage && !isTechnicalDiagnosticMessage(c.lastMessage, c.isSupport === true, supportOperator)
    ? c.lastMessage
    : null;
  if (!last) return '';
  let body: React.ReactNode;
  if (last.type === 99) {
    const d = last.payload?.data as { call?: string; missed?: boolean } | null;
    const icon = d?.call === 'video' ? '/video-call.svg' : '/audio-call.svg';
    body = (
      <span className="preview-icon-row">
        <img src={icon} alt="" className="preview-icon" />
        {i18n.t(d?.missed ? 'chats:preview.missedCall' : 'chats:preview.call')}
      </span>
    );
  } else {
    const mediaList = last.payload?.data?.media ?? [];
    if (mediaList.length > 1) {
      body = (
        <span className="preview-icon-row">
          <img src="/attachments.svg" alt="" className="preview-icon" />
          {i18n.t('chats:preview.attachments', { count: mediaList.length })}
        </span>
      );
    } else {
      const media = mediaList[0];
      const kind = media?.kind;
      if (kind === 'audio') {
        body = (
          <span className="preview-icon-row">
            <img src={media?.fileName ? '/audio-file.svg' : '/mic.svg'} alt="" className="preview-icon" />
            {i18n.t(media?.fileName ? 'chats:preview.audio' : 'chats:preview.voice')}
          </span>
        );
      } else if (kind === 'video') {
        body = (
          <span className="preview-icon-row">
            <img src="/video-file.svg" alt="" className="preview-icon" />
            {i18n.t('chats:preview.video')}
          </span>
        );
      } else if (kind === 'file') {
        body = (
          <span className="preview-icon-row">
            <img src="/document.svg" alt="" className="preview-icon" />
            {i18n.t('chats:preview.file')}
          </span>
        );
      } else if (kind === 'image') {
        body = (
          <span className="preview-icon-row">
            <img src="/photo.svg" alt="" className="preview-icon" />
            {i18n.t('chats:preview.image')}
          </span>
        );
      } else {
        body = last.payload?.data?.text || '';
      }
    }
  }
  // В групповом чате превью начинается с «отправитель:» — как в Telegram.
  if (c.type === 2 && last.senderId) {
    const name = last.senderId === me?.id ? i18n.t('chats:preview.you') : last.senderName || '…';
    return (
      <>
        <span className="chat-item-sender">{name}</span>
        <span className="chat-item-colon">: </span>
        {body}
      </>
    );
  }
  return body;
}

export default function ChatList({ chats, activeId, user, onOpen, onOpenSupport, onRefresh, onLogout, onOpenSettings, onOpenAdmin, foldersVersion, contactsVersion, onOpenMessage, collapsed = false }: Props) {
  const { t } = useTranslation(['chats', 'account', 'dialogs', 'people', 'stories']);
  // Перерисовка списка при изменении черновиков (previewOf читает их напрямую из localStorage).
  useSyncExternalStore(subscribeDrafts, draftsVersion);
  // null — закрыт; 1/2 — открыт сразу на нужном типе (личный/группа), см. FAB-меню ниже.
  const [supportOpen, setSupportOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState<1 | 2 | null>(null);
  // Клик по FAB «Новый чат» открывает маленькое меню выбора (личный/группа) вместо того, чтобы
  // сразу прыгать в модалку с табами (см. отчёт пользователя).
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const fabBtnRef = useRef<HTMLButtonElement>(null);
  const [fabMenuPos, setFabMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  // Позиция меню считается от кнопки и рендерится порталом (см. ниже) — .sidebar
  // имеет overflow:hidden для скролла списка чатов, который в свёрнутом состоянии (76px,
  // режим "чат с комнатами") обрезал бы абсолютно позиционированное меню почти целиком.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [chatMenu, setChatMenu] = useState<ChatSummary & { x: number; y: number } | null>(null);
  // Подтверждение «покинуть группу / удалить чат» — кастомный ConfirmModal вместо нативного
  // confirm() (нативные браузерные диалоги в приложении запрещены, см. feedback_no_native_browser_dialogs).
  const [confirmLeave, setConfirmLeave] = useState<ChatSummary | null>(null);
  // Что показываем в левой колонке: список чатов или экран контактов (из меню).
  const [view, setView] = useState<'chats' | 'contacts'>('chats');
  const wsOnline = useConnectionStatus();

  // Поиск в шапке: мгновенно фильтрует уже загруженный список по названию чата (см. inQuery
  // ниже), а с 2+ символов ещё и запрашивает бэкенд — глобальный поиск по чатам, сообщениям,
  // файлам, медиа, ссылкам и людям во всей базе пользователей, не только среди уже добавленных
  // контактов (см. GlobalSearchResults, SearchService.searchGlobal), сгруппированный по категориям.
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchActive = searchQuery.trim().length >= 2;
  const [searchResult, setSearchResult] = useState<SearchResultDto | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  useEffect(() => {
    if (!searchActive) {
      setSearchResult(null);
      setSearchLoading(false);
      return;
    }
    const q = searchQuery.trim();
    setSearchLoading(true);
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const r = await globalSearch(q);
        // Ответы на устаревший (уже перепечатанный) запрос могут прийти позже свежих —
        // не даём им перезаписать актуальный результат (см. отчёт ревью).
        if (!cancelled) setSearchResult(r);
      } catch {
        if (!cancelled) setSearchResult(null);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [searchQuery, searchActive]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Табы папок чатов — только фильтр видимого списка; создание/переименование/удаление и
  // состав папки — в настройках (см. SettingsModal, вкладка «Папки», отчёт пользователя
  // "работу с папками перенеси в настройки"). Перечитываем при изменении foldersVersion.
  const [folders, setFolders] = useState<ChatFolderDto[]>([]);
  const [activeTab, setActiveTab] = useState<string>('all');
  // Смарт-папки («Личные», «Новые» — см. lib/smartFolders.ts) — какие включены и в каком
  // порядке показывать табы задаётся в настройках, хранится на сервере (см. api/folders.ts
  // getFolderPrefs, отчёт пользователя "перенеси в базу папки" — раньше жило в localStorage
  // и не синхронизировалось между устройствами). Перечитываем при каждом foldersVersion (том
  // же сигнале, что и для обычных папок).
  const [enabledSmart, setEnabledSmart] = useState<Set<SmartFolderId>>(new Set());
  const [folderOrder, setFolderOrderState] = useState<string[]>([]);
  useEffect(() => {
    Promise.all([listFolders(), getFolderPrefs()])
      .then(([list, prefs]) => {
        const smart = new Set(
          prefs.enabledSmart.filter((id): id is SmartFolderId => SMART_FOLDERS.some((f) => f.id === id)),
        );
        setFolders(list);
        setEnabledSmart(smart);
        setFolderOrderState(prefs.folderOrder);
        setActiveTab((prev) => {
          if (prev === 'all') return prev;
          const smartMeta = SMART_FOLDERS.find((f) => f.id === prev);
          if (smartMeta) return smart.has(smartMeta.id) ? prev : 'all';
          return list.some((f) => f.id === prev) ? prev : 'all';
        });
      })
      .catch(() => {
        /* не критично */
      });
  }, [foldersVersion]);

  // Личные контакты (см. api/contacts.ts): экран в левой колонке, открывается из меню.
  const [contacts, setContacts] = useState<UserBriefDto[]>([]);
  const [contactAddOpen, setContactAddOpen] = useState(false);
  const [showMyQr, setShowMyQr] = useState(false);
  const [contactQuery, setContactQuery] = useState('');
  const [contactResults, setContactResults] = useState<UserBriefDto[]>([]);
  const [contactMenu, setContactMenu] = useState<(UserBriefDto & { x: number; y: number }) | null>(null);

  // Сторисы (истории) — кольца авторов внутри строки поиска, просмотрщик, публикация.
  const [storyAuthors, setStoryAuthors] = useState<StoryAuthorDto[]>([]);
  // Ряд сторис сворачивается/разворачивается по клику на заголовок (см. отчёт пользователя
  // "почему сторисы не сворачиваются обратно") — компактный заголовок с мини-аватарками,
  // большой ряд с кольцами — только в развёрнутом виде.
  const [storyStripOpen, setStoryStripOpen] = useState(false);
  // Лента сторис: авторы с непросмотренными — в начале, полностью просмотренные — в конец
  // (см. отчёт пользователя "просмотренные ранее сторис убирать в конец и не воспроизводить
  // их заново"), замьюченные авторы ("Не показывать сторис [контакт]", см. StoryViewer)
  // отфильтрованы совсем. viewed/muted теперь приходят с сервера прямо в storyAuthors (см.
  // api/stories.ts) — при мьюте/новом просмотре компонент просто перезапрашивает loadStories(),
  // без отдельного счётчика-костыля для инвалидации (как было с localStorage). Индексы из
  // этого массива идут в StoryViewer и в кольца-полоски — именно этот массив, а не
  // storyAuthors напрямую, иначе индекс кольца не совпадал бы с индексом в ленте просмотрщика
  // при другом порядке сортировки.
  const storyFeedSorted = useMemo(() => {
    return storyAuthors
      .filter((a) => !a.muted)
      .sort((a, b) => {
        const au = a.stories.some((s) => !s.viewed);
        const bu = b.stories.some((s) => !s.viewed);
        return (bu ? 1 : 0) - (au ? 1 : 0);
      });
  }, [storyAuthors]);

  // Автосворачивание большого ряда сторис: занялись чем-то другим (клик вне ряда и вне колец
  // в поиске) — ряд сворачивается обратно в строку поиска (см. отчёт пользователя "если
  // займешься чем-либо ещё — сворачивай строку со сторисами обратно в строку поиска").
  useEffect(() => {
    if (!storyStripOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // Клики внутри ряда, по кольцам в поиске и внутри просмотрщика (закрытие сторис по ✕
      // не должно сворачивать панель — см. отчёт пользователя "при закрытии сториса по
      // крестику не сворачивать панель сторисов") — не сворачиваем.
      if (t.closest('[data-story-strip]') || t.closest('.search-stories') || t.closest('.story-viewer')) return;
      setStoryStripOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [storyStripOpen]);
  const [storyViewerStart, setStoryViewerStart] = useState<number | null>(null);
  // Меню добавления сторис (Фото/Видео/Аудио/Записать аудио/Записать видео — пять строк с
  // иконками, см. отчёт пользователя) — открывается и с мини-колец в строке поиска, и с
  // большой кнопки "+" в развёрнутом ряду сторис, поэтому позиция считается от места клика.
  const [storyAttachMenuOpen, setStoryAttachMenuOpen] = useState(false);
  const [storyAttachMenuPos, setStoryAttachMenuPos] = useState({ left: 0, top: 0 });
  const [storyRecordKind, setStoryRecordKind] = useState<'audio' | 'video' | null>(null);
  // Клэмп позиции контекстного меню к границам экрана — общий подход с openStoryAttachMenu
  // ниже, применяется и к chatMenu/contactMenu (правый клик у нижнего/правого края иначе
  // уводил меню за пределы вьюпорта, см. отчёт ревью).
  const clampMenuPos = (x: number, y: number, w = 190, h = 180) => ({
    x: Math.max(4, Math.min(x, window.innerWidth - w - 4)),
    y: Math.max(4, Math.min(y, window.innerHeight - h - 4)),
  });
  const openStoryAttachMenu = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const MENU_W = 190;
    const MENU_H = 216;
    const left = Math.max(4, Math.min(rect.left, window.innerWidth - MENU_W - 4));
    const top = Math.min(rect.bottom + 6, window.innerHeight - MENU_H - 4);
    setStoryAttachMenuPos({ left, top });
    setStoryAttachMenuOpen(true);
  };
  const loadStories = async () => {
    try {
      setStoryAuthors(await storyFeed());
    } catch {
      /* не критично */
    }
  };
  useEffect(() => {
    loadStories();
  }, []);
  // Обрезка видео-сторис (см. отчёт пользователя "при загрузке автоматически проводить
  // обрезку либо дать возможность обрезать самим"): для видео открывается VideoTrimEditor
  // (тот же, что для видео-сообщений), после обрезки загружаем с trim. Фото — как есть.
  const [storyTrimFile, setStoryTrimFile] = useState<File | null>(null);
  const [storyUploadBusy, setStoryUploadBusy] = useState(false);
  const [pendingStoryRetry, setPendingStoryRetry] = useState<{ file: File; trim?: { start: number; end: number }; error: string } | null>(null);
  const uploadStoryFile = async (file: File, trim?: { start: number; end: number }) => {
    if (storyUploadBusy) return;
    setStoryUploadBusy(true);
    try {
      const prepared = !trim && file.type.startsWith('image/') ? await compressPhotoForUpload(file) : file;
      const media = trim
        ? await uploadMediaWithProgress(prepared, undefined, () => {}, trim)
        : await uploadMedia(prepared);
      if (trim) await publishStoryVideo(media.id);
      else await publishStory(media.id);
      setPendingStoryRetry(null);
      loadStories();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('stories:uploadError');
      setPendingStoryRetry({ file, trim, error: message });
      showTransferNotice(message);
    } finally {
      setStoryUploadBusy(false);
    }
  };
  const onStoryFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type.startsWith('video/')) {
      setStoryTrimFile(file);
      return;
    }
    void uploadStoryFile(file);
  };
  const publishStoryTrimmed = async (trim: { start: number; end: number } | null) => {
    if (!storyTrimFile) return;
    const file = storyTrimFile;
    setStoryTrimFile(null);
    // null — метаданные видео не загрузились в редакторе обрезки: закрываем без публикации,
    // вместо того чтобы отправить {start:0,end:0} (сервер обрезал бы клип до нулевой длины).
    if (!trim) return;
    try {
      const m = await uploadMediaWithProgress(file, undefined, () => {}, trim);
      // publishStoryVideo сам решает: короткий выбранный диапазон — одна сторис, длинный —
      // сервер порежет на сегменты установленной длины (см. отчёт пользователя "дать
      // возможность размещать видео более длительного размера, но делить на сторисы
      // установленной длины") — ручная обрезка диапазона при этом никуда не делась.
      await publishStoryVideo(m.id);
      loadStories();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('stories:uploadError');
      setPendingStoryRetry({ file, trim, error: message });
      showTransferNotice(message);
    }
  };

  const loadContacts = async () => {
    try {
      setContacts(await listContacts());
    } catch {
      /* не критично */
    }
  };
  useEffect(() => {
    loadContacts();
  }, [contactsVersion]);
  useEffect(() => {
    const q = contactQuery.trim();
    if (!contactAddOpen || q.length < 2) {
      setContactResults([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const r = await searchUsers(q);
        // См. аналогичный guard у глобального поиска выше — устаревший ответ не должен
        // перезаписать результат более свежего запроса.
        if (!cancelled) setContactResults(r.filter((u) => u.id !== user?.id));
      } catch {
        if (!cancelled) setContactResults([]);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactQuery, contactAddOpen]);
  const [startingContactId, setStartingContactId] = useState<string | null>(null);
  const openContactChat = async (id: string) => {
    // Guard от повторного клика/тапа по одному и тому же контакту, пока запрос ещё в
    // полёте — без этого двойной клик успевал уйти как два параллельных createChat и
    // раньше приводил к дублю личного чата (см. отчёт про "Алексей Поляков").
    if (startingContactId === id) return;
    setStartingContactId(id);
    try {
      const chat = await createChat(1, null, [id]);
      onRefresh();
      onOpen(chat.id);
    } catch {
      /* не критично — просто не откроется в этот раз */
    } finally {
      setStartingContactId(null);
    }
  };

  // Закреплённые — наверх (последние закреплённые первыми, как отдаёт бэкенд — см.
  // ChatService.list), дальше — чат с самым свежим сообщением.
  const sorted = [...chats].sort((a, b) => {
    if (a.pinnedAt && b.pinnedAt) return new Date(b.pinnedAt).getTime() - new Date(a.pinnedAt).getTime();
    if (a.pinnedAt) return -1;
    if (b.pinnedAt) return 1;
    const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    return bt - at;
  });

  const mainChats = sorted.filter((c) => !c.archived);
  const archivedChats = sorted.filter((c) => c.archived);

  // Табы — смарт-папки (включённые в настройках) + обычные папки, в сохранённом пользователем
  // порядке (см. lib/smartFolders.ts sortByFolderOrder; «Все» всегда первый и в порядок не
  // входит). Каждому табу заранее считаем список чатов — используется и для фильтрации, и
  // для бейджа непрочитанных.
  const tabItems = sortByFolderOrder([
    // Пустая папка не должна отображаться (см. отчёт пользователя) — то же самое правило
    // применено и к смарт-папкам, кроме «Новые» (см. SMART_FOLDERS.hideWhenEmpty — вкладка
    // то появлялась, то пропадала при каждом прочтении последнего сообщения).
    ...SMART_FOLDERS.filter((f) => enabledSmart.has(f.id))
      .map((f) => ({
        key: f.id as string,
        label: t(f.id === 'personal' ? 'tabs.personal' : 'tabs.unread'),
        chats: mainChats.filter(f.predicate),
        hideWhenEmpty: f.hideWhenEmpty,
      }))
      .filter((f) => !f.hideWhenEmpty || f.chats.length > 0),
    ...folders.filter((f) => f.chatIds.length > 0).map((f) => ({
      key: f.id,
      label: f.name,
      chats: mainChats.filter((c) => f.chatIds.includes(c.id)),
    })),
  ], folderOrder);

  // "Бегущая" pill-подсветка активного таба — та же техника, что у вкладок инфопанели
  // (ChatInfoModal: .info-tabs-pill), чтобы визуально совпадать с ней (отчёт пользователя
  // "папки не смещаются к центру", см. .folder-tabs в theme.css).
  const folderTabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [folderPillRect, setFolderPillRect] = useState<{ left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    const el = folderTabRefs.current.get(activeTab);
    setFolderPillRect(el ? { left: el.offsetLeft, width: el.offsetWidth } : null);
  }, [activeTab, tabItems.length]);

  // Таб мог только что опустеть и пропасть из tabItems (например, смарт-папка «Новые» после
  // прочтения последнего сообщения) — без явного сброса inTab() ниже молча показал бы вообще
  // ВСЕ чаты (tabItems.find не находит текущий activeTab), а сам таб-бар выглядел бы так,
  // будто ни один таб не выбран.
  useEffect(() => {
    if (activeTab !== 'all' && !tabItems.some((t) => t.key === activeTab)) setActiveTab('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabItems.map((t) => t.key).join(',')]);

  // Фильтры списка: активный таб + строка поиска.
  const q = searchQuery.trim().toLowerCase();
  const inTab = (c: ChatSummary) => {
    if (activeTab === 'all') return true;
    const item = tabItems.find((t) => t.key === activeTab);
    return item ? item.chats.includes(c) : true;
  };
  const inQuery = (c: ChatSummary) => !q || (c.title ?? '').toLowerCase().includes(q);
  const visibleMain = mainChats.filter((c) => inTab(c) && inQuery(c));
  // Бейдж таба — сумма непрочитанных сообщений по его чатам (как в Telegram), а не количество
  // самих чатов (отчёт пользователя "должно отображать только количество непрочитанных").
  const unreadSum = (list: ChatSummary[]) => list.reduce((sum, c) => sum + c.unreadCount, 0);

  const togglePin = async () => {
    if (!chatMenu) return;
    const { id, pinnedAt } = chatMenu;
    setChatMenu(null);
    try {
      await (pinnedAt ? unpinChat(id) : pinChat(id));
      onRefresh();
    } catch {
      /* не критично — список просто не обновится */
    }
  };
  const [archiveState, dispatchArchive] = useReducer(chatArchiveReducer, initialChatArchiveState);
  const archiveOpen = archiveState.buttonVisible;
  const archiveTouch = useRef<{ x: number; y: number } | null>(null);
  const archiveWheel = useRef(0);
  const archiveWheelTime = useRef(0);
  const chatListRef = useRef<HTMLDivElement>(null);
  const setArchiveOpen = (open: boolean) => dispatchArchive(open ? 'reveal' : 'hide');
  useEffect(() => { dispatchArchive('back'); }, [user?.id]);
  useEffect(() => {
    dispatchArchive('hide');
    archiveWheel.current = 0;
    archiveTouch.current = null;
  }, [activeId, view, searchQuery, archivedChats.length]);
  useEffect(() => {
    const hide = () => dispatchArchive('hide');
    const onVisibility = () => { if (document.hidden) hide(); };
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target as Element).closest('[data-archive-control]')) hide();
    };
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const toggleArchive = async () => {
    if (!chatMenu) return;
    const { id, archived } = chatMenu;
    setChatMenu(null);
    try {
      await (archived ? unarchiveChat(id) : archiveChat(id));
      onRefresh();
    } catch {
      /* не критично — список просто не обновится */
    }
  };

  const chatRow = (c: ChatSummary) => (
    <div
      key={c.id}
      className={'chat-item' + (c.id === activeId ? ' active' : '')}
      onClick={() => onOpen(c.id)}
      onContextMenu={(e) => {
        e.preventDefault();
        setChatMenu({ ...c, ...clampMenuPos(e.clientX, e.clientY) });
      }}
    >
      <Avatar
        userId={c.otherUserId}
        chatId={c.type === 2 || !c.otherUserId ? c.id : null}
        name={c.title || '?'}
        version={c.avatarFileId}
        size={54}
      />
      {/* Видна только в свёрнутом режиме (см. .sidebar-collapsed в theme.css) — там имя/превью
          скрыты, а бейдж непрочитанного из .chat-item-body ушёл бы вместе с ними. */}
      {c.unreadCount > 0 && <span className="chat-item-rail-dot" />}
      <div className="chat-item-body">
        <div className="chat-item-row">
          <span className="chat-item-title">{c.title || t('fallback.chat')}</span>
          {isChatMutedAt(c) && <span className="chat-item-muted" title={t('mutedStatus')} aria-label={t('mutedStatus')}><NotificationIcon variant="off" size={14} /></span>}
          <span className="chat-item-time">{timeOf(c.lastMessageAt)}</span>
        </div>
        <div className="chat-item-row">
          <span className="chat-item-preview">{previewOf(c, user)}</span>
          <span className="chat-item-badges">
            {c.unreadCount > 0 && <span className="unread">{c.unreadCount > 99 ? '99+' : c.unreadCount}</span>}
          </span>
        </div>
      </div>
    </div>
  );

  // «Покинуть группу / Удалить чат» — открывает кастомный ConfirmModal, по подтверждению выходит.
  // Владелец группы с другими участниками вместо простого подтверждения получает выбор:
  // передать права и выйти или удалить группу (см. OwnerLeaveModal, ChatService.removeMember).
  const [ownerLeave, setOwnerLeave] = useState<{ chat: ChatSummary; others: MemberDto[] } | null>(null);
  const [ownerLeaveBusy, setOwnerLeaveBusy] = useState(false);
  const [ownerLeaveError, setOwnerLeaveError] = useState('');
  const askLeaveChat = async () => {
    if (!chatMenu) return;
    const chat = chatMenu;
    setChatMenu(null);
    if (chat.type === 2 && user) {
      try {
        const members = await listMembers(chat.id);
        const others = members.filter((m) => m.user.id !== user.id);
        if (members.find((m) => m.user.id === user.id)?.role === 1 && others.length > 0) {
          setOwnerLeaveError('');
          setOwnerLeave({ chat, others });
          return;
        }
      } catch {
        /* не удалось узнать роль — обычное подтверждение, сервер сам назначит преемника */
      }
    }
    setConfirmLeave(chat);
  };
  const doLeaveChat = async () => {
    if (!confirmLeave || !user) return;
    const { id } = confirmLeave;
    setConfirmLeave(null);
    try {
      await removeMember(id, user.id);
      onRefresh();
    } catch {
      /* не критично — список просто не обновится */
    }
  };
  const doOwnerLeave = async (action: () => Promise<unknown>) => {
    setOwnerLeaveBusy(true);
    setOwnerLeaveError('');
    try {
      await action();
      setOwnerLeave(null);
      onRefresh();
    } catch (err) {
      setOwnerLeaveError(err instanceof ApiError ? err.message : t('context.ownerActionError'));
    } finally {
      setOwnerLeaveBusy(false);
    }
  };

  // Общие модалки для обоих экранов (новый чат, просмотрщик сторис, файл публикации).
  const sharedModals = (
    <>
      {supportOpen && user && <SupportConversations
        key={user.id + ':' + user.role}
        operator={user.role === 'admin' || user.role === 'support'}
        onClose={() => setSupportOpen(false)}
        onOpen={async (chat, applicant) => {
          await onOpenSupport(chat, applicant);
          setSupportOpen(false);
        }}
      />}
      {storyAttachMenuOpen && (
        <>
          <div className="menu-backdrop" onClick={() => setStoryAttachMenuOpen(false)} />
          <div
            className="attach-menu"
            /* .attach-menu в CSS для композера задаёт ещё и bottom: 54px (меню растёт вверх от
               поля ввода) — тут своя позиция сверху, bottom нужно сбросить явно, иначе при
               одновременно заданных top и bottom высота растягивается на весь промежуток между
               ними (см. отчёт пользователя "меню прикрепления в сторис растянулось на весь
               экран"). */
            style={{ position: 'fixed', left: storyAttachMenuPos.left, top: storyAttachMenuPos.top, bottom: 'auto' }}
          >
            <label className="attach-menu-item">
              <img src="/photo.svg" alt="" /> {t('stories:photo')}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setStoryAttachMenuOpen(false);
                  onStoryFile(e);
                }}
              />
            </label>
            <label className="attach-menu-item">
              <img src="/video-call.svg" alt="" /> {t('stories:video')}
              <input
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setStoryAttachMenuOpen(false);
                  onStoryFile(e);
                }}
              />
            </label>
            <label className="attach-menu-item">
              <img src="/audio-file.svg" alt="" /> {t('stories:audio')}
              <input
                type="file"
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setStoryAttachMenuOpen(false);
                  onStoryFile(e);
                }}
              />
            </label>
            <button
              type="button"
              className="attach-menu-item"
              onClick={() => {
                setStoryAttachMenuOpen(false);
                setStoryRecordKind('audio');
              }}
            >
              <img src="/mic.svg" alt="" /> {t('stories:recordAudio')}
            </button>
            <button
              type="button"
              className="attach-menu-item"
              onClick={() => {
                setStoryAttachMenuOpen(false);
                setStoryRecordKind('video');
              }}
            >
              <img src="/video-call.svg" alt="" /> {t('stories:recordVideo')}
            </button>
          </div>
        </>
      )}
      {storyRecordKind && (
        <Suspense fallback={null}>
          <StoryRecorder
            kind={storyRecordKind}
            onClose={() => setStoryRecordKind(null)}
            onPublished={loadStories}
          />
        </Suspense>
      )}
      {newChatOpen != null && (
        <NewChat
          initialType={newChatOpen}
          onClose={() => setNewChatOpen(null)}
          onCreated={(id) => {
            setNewChatOpen(null);
            onRefresh();
            onOpen(id);
          }}
        />
      )}
      {storyViewerStart != null && (
        <StoryViewer
          feed={storyFeedSorted}
          start={storyViewerStart}
          onClose={() => {
            setStoryViewerStart(null);
            // Просмотры отмечались локально внутри StoryViewer и фоном на сервере (см. его
            // markStoryViewed-эффект) — перечитываем ленту, иначе кольцо/сортировка тут
            // остались бы со старыми viewed:false до следующего полного обновления списка.
            loadStories();
          }}
          onDeleted={loadStories}
          onMuted={loadStories}
        />
      )}
      {pendingStoryRetry && (
        <ConfirmModal
          message={t('stories:retryTransfer', { error: pendingStoryRetry.error, name: pendingStoryRetry.file.name })}
          confirmLabel={t('stories:retry')}
          onConfirm={() => {
            const retry = pendingStoryRetry;
            setPendingStoryRetry(null);
            void uploadStoryFile(retry.file, retry.trim);
          }}
          onCancel={() => setPendingStoryRetry(null)}
        />
      )}
      {storyTrimFile && (
        <Suspense fallback={null}>
          <VideoTrimEditor file={storyTrimFile} onSave={publishStoryTrimmed} />
        </Suspense>
      )}
      {ownerLeave && user && (
        <OwnerLeaveModal
          groupTitle={ownerLeave.chat.title || t('fallback.group')}
          others={ownerLeave.others}
          busy={ownerLeaveBusy}
          error={ownerLeaveError}
          onTransferAndLeave={(id) => void doOwnerLeave(() => removeMember(ownerLeave.chat.id, user.id, id))}
          onDeleteGroup={() => void doOwnerLeave(() => deleteChat(ownerLeave.chat.id))}
          onCancel={() => setOwnerLeave(null)}
        />
      )}
      {confirmLeave && (
        <ConfirmModal
          message={
            confirmLeave.type === 2
              ? t('context.leaveGroupConfirm', { title: confirmLeave.title || t('fallback.chat') })
              : t('context.deleteChatConfirm', { title: confirmLeave.title || t('fallback.chat') })
          }
          confirmLabel={confirmLeave.type === 2 ? t('context.leave') : t('context.delete')}
          onCancel={() => setConfirmLeave(null)}
          onConfirm={doLeaveChat}
        />
      )}
    </>
  );

  // ---- Экран «Контакты» (Telegram Web: аватар 54px + статус, FAB «добавить контакт») ----
  if (view === 'contacts') {
    return (
      <aside className="sidebar">
        <header className="left-header">
          <button className="header-btn" title={t('people:contacts.backToChats')} onClick={() => setView('chats')}>
            <img src="/back.svg" alt={t('people:contacts.back')} />
          </button>
          <div className="contacts-screen-title">{t('people:contacts.title')}</div>
          <button className="header-btn" title={t('people:contacts.myQr')} onClick={() => setShowMyQr(true)}>
            <img src="/scan-qr-code.svg" alt={t('people:contacts.myQr')} />
          </button>
          <button className="header-btn" title={t('people:contacts.add')} onClick={() => setContactAddOpen(!contactAddOpen)}>
            <img src="/profile.svg" alt={t('people:contacts.add')} />
          </button>
        </header>

        {contactAddOpen && (
          <div style={{ padding: '0 12px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              className="input" style={{ width: '100%' }} autoFocus
              placeholder={t('people:contacts.search')}
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
            />
            {contactResults.map((u) => (
              <div key={u.id} className="contact-result-row">
                <Avatar userId={u.id} name={u.displayName || u.username} size={26} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                  {u.displayName || u.username}
                </span>
                <button
                  className="call-mini"
                  title={t('people:contacts.addAction')}
                  onClick={async () => {
                    try {
                      await addContact(u.id);
                      setContactQuery('');
                      setContactAddOpen(false);
                      loadContacts();
                    } catch {
                      /* не критично */
                    }
                  }}
                >
                  +
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="scroll-list" style={{ flex: 1, overflowY: 'auto' }}>
          {contacts.length === 0 && (
            <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>{t('people:contacts.empty')}</div>
          )}
          {contacts.map((c) => (
            <div
              key={c.id}
              className="chat-item"
              style={startingContactId === c.id ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
              onClick={() => openContactChat(c.id)}
              onContextMenu={(e) => {
                e.preventDefault();
                setContactMenu({ ...c, ...clampMenuPos(e.clientX, e.clientY) });
              }}
            >
              <Avatar userId={c.id} name={c.displayName || c.username} size={54} />
              <div className="chat-item-body">
                <div className="chat-item-row">
                  <span className="chat-item-title">{c.displayName || c.username}</span>
                </div>
                <div className="chat-item-row">
                  <span className="chat-item-preview">
                    {c.lastOnlineAt ? formatPresence({ online: false, lastOnlineAt: c.lastOnlineAt }) : ''}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button className="fab-new-chat" title={t('people:contacts.add')} onClick={() => setContactAddOpen(!contactAddOpen)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="M2.5 20c0-3.5 3-5.5 6.5-5.5s6.5 2 6.5 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M18 8v6M15 11h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        {contactMenu && (
          <>
            <div className="menu-backdrop" onClick={() => setContactMenu(null)} />
            <div className="chat-ctx-menu" style={{ left: contactMenu.x, top: contactMenu.y }}>
              <button className="menu-item" onClick={() => { setContactMenu(null); openContactChat(contactMenu.id); }}>
                <img src="/profile.svg" alt="" /> {t('people:contacts.openChat')}
              </button>
              <button
                className="menu-item danger"
                onClick={async () => {
                  const id = contactMenu.id;
                  setContactMenu(null);
                  try {
                    await removeContact(id);
                    loadContacts();
                  } catch {
                    /* не критично */
                  }
                }}
              >
                <img src="/trash.svg" alt="" /> {t('people:contacts.remove')}
              </button>
            </div>
          </>
        )}

        {sharedModals}
        {showMyQr && user && <MyContactQrModal me={user} onClose={() => setShowMyQr(false)} />}
      </aside>
    );
  }

  // ---- Список чатов ----
  return (
    <aside className={'sidebar' + (collapsed && !archiveState.screenOpen ? ' sidebar-collapsed' : '')}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) dispatchArchive('hide');
      }}>
      {archiveState.screenOpen ? (
        <header className="left-header">
          <button type="button" className="header-btn" title={t('people:contacts.backToChats')}
            onClick={() => dispatchArchive('back')}>
            <img src="/back.svg" alt={t('people:contacts.back')} />
          </button>
          <div className="contacts-screen-title">{t('archive.title')}</div>
        </header>
      ) : (<>
      {/* Заголовок и вторичные действия одинаковы в браузере и установленном PWA. */}
      <header className="left-header chat-list-header">
        <div className="contacts-screen-title chat-list-title">HomeChat</div>
        {archivedChats.length > 0 && (
          <button type="button" className="header-btn" data-archive-control title={t(archiveOpen ? 'archive.hide' : 'archive.show')}
            aria-label={t(archiveOpen ? 'archive.hide' : 'archive.show')} aria-expanded={archiveOpen}
            onClick={() => {
              if (!archiveOpen && chatListRef.current) chatListRef.current.scrollTop = 0;
              setArchiveOpen(!archiveOpen);
            }}>
            <img src="/bookmark.svg" alt="" />
          </button>
        )}
        <div className="menu-wrap">
          <button
            ref={menuBtnRef}
            className="header-btn"
            title={t('menu.title')}
            aria-label={t('menu.title')}
            aria-expanded={menuOpen}
            onClick={() => {
              if (menuOpen) { setMenuOpen(false); return; }
              const r = menuBtnRef.current?.getBoundingClientRect();
              if (r) setMenuPos(clampMenuPos(r.left, r.bottom + 6, 190, 280));
              setMenuOpen(true);
            }}
          >
            <img src="/ellipsis-vertical.svg" alt={t('menu.title')} />
          </button>
          {menuOpen && menuPos && createPortal(
            <>
              <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
              {/* menu-fixed: портал в document.body, позиция — inline style от menuPos (см. выше,
                  зачем это нужно — обрезка overflow:hidden свёрнутого сайдбара). */}
              <div className="menu menu-fixed" style={{ left: menuPos.x, top: menuPos.y }}>
                <button
                  type="button"
                  className="menu-user menu-user-btn"
                  onClick={() => { setMenuOpen(false); onOpenSettings('account'); }}
                >
                  <Avatar userId={user?.id} name={user?.displayName ?? '?'} version={user?.avatarFileId} size={40} />
                  <div className="menu-user-name">{user?.displayName}</div>
                </button>
                <button className="menu-item" aria-expanded={searchOpen} onClick={() => {
                  setMenuOpen(false);
                  setSearchOpen(true);
                  requestAnimationFrame(() => searchInputRef.current?.focus());
                }}>
                  <img src="/search.svg" alt="" /> {t('search.placeholder')}
                </button>
                <button className="menu-item" onClick={() => { setMenuOpen(false); setNewChatOpen(1); }}>
                  <img src="/edit.svg" alt="" /> {t('menu.newChat')}
                </button>
                <button className="menu-item" onClick={() => { setMenuOpen(false); setView('contacts'); }}>
                  <img src="/profile.svg" alt="" /> {t('menu.contacts')}
                </button>
                <button className="menu-item" onClick={() => { setMenuOpen(false); setSupportOpen(true); }}>
                  <img src="/message-circle.svg" alt="" /> {t(user?.role === 'admin' || user?.role === 'support' ? 'supportConversations' : 'writeSupport')}
                </button>
                {(user?.role === 'admin' || user?.role === 'support') && (
                  <button className="menu-item" onClick={() => { setMenuOpen(false); onOpenAdmin(); }}>
                    <img src="/admin.svg" alt="" /> {t('menu.admin')}
                  </button>
                )}
                <button
                  className="menu-item"
                  onClick={async () => {
                    setMenuOpen(false);
                    try {
                      const chat = await getOrCreateNotesChat();
                      onRefresh();
                      onOpen(chat.id);
                    } catch {
                      /* не критично — просто не откроется в этот раз */
                    }
                  }}
                >
                  <img src="/bookmark.svg" alt="" /> {t('menu.saved')}
                </button>
                <button className="menu-item" onClick={() => { setMenuOpen(false); onOpenSettings(); }}>
                  <img src="/settings.svg" alt="" /> {t('menu.settings')}
                </button>
                <button className="menu-item" onClick={() => { setMenuOpen(false); onRefresh(); }}><img src="/refresh-cw.svg" alt="" /> {t('menu.refresh')}</button>
                <button className="menu-item danger" onClick={() => { setMenuOpen(false); onLogout(); }}>{t('menu.logout')}</button>
              </div>
            </>,
            document.body,
          )}
        </div>

      </header>
        <div
          className={'search-input-wrap chat-list-search' + (searchOpen ? ' chat-search-open' : '') + (searchFocused ? ' focused' : '')}
          onClick={() => searchInputRef.current?.focus()}
        >
          {searchFocused || searchQuery ? (
            <img src="/search.svg" className="search-in-icon" alt="" />
          ) : (
            /* Кольца сторис в строке поиска (как раньше) — клик по ним разворачивает большой
               ряд сторис между папками и чатами (см. отчёт пользователя "восстанови сторисы в
               строке поиска, по их нажатию открывай строку с увеличенными сторисами"). */
            <div className="search-stories" onClick={(e) => e.stopPropagation()}>
              <button
                className="search-story-ring mine"
                title={t('stories:addStory')}
                onClick={openStoryAttachMenu}
              >
                <Avatar userId={user?.id} name={user?.displayName ?? '?'} size={22} />
                <span className="search-story-add">+</span>
              </button>
              {storyFeedSorted.slice(0, 4).map((a) => (
                <button
                  key={a.user.id}
                  className="search-story-ring"
                  title={a.user.displayName}
                  onClick={() => setStoryStripOpen(true)}
                >
                  <Avatar userId={a.user.id} name={a.user.displayName} size={22} />
                </button>
              ))}
            </div>
          )}
          <input
            ref={searchInputRef}
            className="search-input"
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {(searchQuery || searchOpen) && (
            <button
              className="search-clear"
              title={t('search.clear')}
              onClick={() => {
                setSearchQuery('');
                setSearchOpen(false);
                searchInputRef.current?.blur();
              }}
            >
              ✕
            </button>
          )}
        </div>
      </>)}

      {!wsOnline && <ConnectingBadge className="sidebar-connection-status sidebar-offline-banner" />}

      {archiveState.screenOpen ? (
        <div className="scroll-list" style={{ flex: 1, overflowY: 'auto' }}>
          {archivedChats.length === 0 && (
            <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>{t('archive.empty')}</div>
          )}
          {archivedChats.map(chatRow)}
        </div>
      ) : searchActive ? (
        <GlobalSearchResults
          result={searchResult}
          loading={searchLoading}
          onOpenChat={(id) => { setSearchQuery(''); onOpen(id); }}
          onOpenMessage={(chatId, messageId) => { setSearchQuery(''); onOpenMessage(chatId, messageId); }}
          onOpenContact={(id) => { setSearchQuery(''); openContactChat(id); }}
        />
      ) : (
        <>
      <BirthdayBanner userId={user?.id} onOpenContact={openContactChat} />

      {/* Табы папок чатов — только фильтр видимого списка (создание/переименование/удаление,
          состав папки — в настройках, см. SettingsModal, вкладка «Папки»). */}
      <div className="folder-tabs">
        {folderPillRect && (
          <div
            className="folder-tabs-pill"
            style={{ transform: `translateX(${folderPillRect.left}px)`, width: folderPillRect.width }}
          />
        )}
        <button
          ref={(el) => { if (el) folderTabRefs.current.set('all', el); }}
          className={'folder-tab' + (activeTab === 'all' ? ' active' : '')}
          onClick={() => setActiveTab('all')}
        >
          {t('tabs.all')}
          {unreadSum(mainChats) > 0 && <span className="folder-tab-count">{unreadSum(mainChats) > 99 ? '99+' : unreadSum(mainChats)}</span>}
        </button>
        {tabItems.map((t) => (
          <button
            key={t.key}
            ref={(el) => { if (el) folderTabRefs.current.set(t.key, el); }}
            className={'folder-tab' + (activeTab === t.key ? ' active' : '')}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
            {unreadSum(t.chats) > 0 && <span className="folder-tab-count">{unreadSum(t.chats) > 99 ? '99+' : unreadSum(t.chats)}</span>}
          </button>
        ))}
      </div>

      {/* Большой ряд сторис — разворачивается по клику на кольца в строке поиска, сворачивается
          сам по себе при клике на что-либо ещё (см. useEffect с document click выше — отчёт
          пользователя "сторис должен сворачиваться, как только клик на что-то другое помимо
          сторисов"; отдельная кнопка-крестик убрана, ручного закрытия не нужно). Просмотренные
          авторы — в конец, полоски полупрозрачные = просмотрено. */}
      {storyStripOpen && (
        <div className="story-strip" data-story-strip>
          <button
            className="story-strip-item mine"
            title={t('stories:addStory')}
            onClick={openStoryAttachMenu}
          >
            <Avatar userId={user?.id} name={user?.displayName ?? '?'} size={56} />
            <span className="story-strip-add">+</span>
          </button>
          {storyFeedSorted.map((a, i) => (
            <button
              key={a.user.id}
              className="story-strip-item"
              title={a.user.displayName}
              onClick={() => setStoryViewerStart(i)}
            >
              <StoryRing stories={a.stories}>
                <Avatar userId={a.user.id} name={a.user.displayName} size={56} />
              </StoryRing>
            </button>
          ))}
        </div>
      )}

      <div
        ref={chatListRef}
        className="scroll-list"
        style={{ flex: 1, overflowY: 'auto', overscrollBehaviorY: 'contain', overflowAnchor: 'none' }}
        onScroll={(event) => {
          if (event.currentTarget.scrollTop > 0) setArchiveOpen(false);
        }}
        onWheel={(event) => {
          if (event.deltaY > 0) setArchiveOpen(false);
          if (archiveOpen || !archivedChats.length || event.currentTarget.scrollTop > 0 || event.deltaY >= 0) {
            archiveWheel.current = 0;
            return;
          }
          if (event.timeStamp - archiveWheelTime.current > 250) archiveWheel.current = 0;
          archiveWheelTime.current = event.timeStamp;
          archiveWheel.current += -event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? event.currentTarget.clientHeight : 1);
          if (shouldRevealArchive(event.currentTarget.scrollTop, event.deltaX, archiveWheel.current)) {
            setArchiveOpen(true);
            archiveWheel.current = 0;
          }
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          archiveTouch.current = event.currentTarget.scrollTop <= 0 && touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchMove={(event) => {
          const start = archiveTouch.current;
          const touch = event.touches[0];
          if (start && touch && touch.clientY < start.y) setArchiveOpen(false);
          if (!archiveOpen && archivedChats.length && start && touch
            && shouldRevealArchive(event.currentTarget.scrollTop, touch.clientX - start.x, touch.clientY - start.y)) {
            setArchiveOpen(true);
          }
        }}
        onTouchEnd={() => { archiveTouch.current = null; }}
        onTouchCancel={() => { archiveTouch.current = null; }}
      >
        {archiveOpen && archivedChats.length > 0 && (
          <button type="button" className="archive-toggle" data-archive-control
            onClick={() => dispatchArchive('open')}>
            {t('archive.button', { count: archivedChats.length })}
          </button>
        )}
        {visibleMain.length === 0 && (
          <div style={{ padding: 24, color: 'var(--muted)', textAlign: 'center' }}>
            {q
              ? t('empty.search')
              : activeTab === 'all'
                ? t('empty.all')
                : activeTab === 'personal'
                  ? t('empty.personal')
                  : t('empty.folder')}
          </div>
        )}
        {visibleMain.map(chatRow)}
      </div>
        </>
      )}

      {/* FAB «Новый чат», как в Telegram Web. Клик открывает маленькое меню выбора
          личный/группа вместо того, чтобы сразу прыгать в модалку с табами (см. отчёт
          пользователя) — портал в document.body по той же причине, что и у гамбургер-меню
          выше: FAB внутри .sidebar (overflow:hidden), меню иначе обрезалось бы. */}
      {!archiveState.screenOpen && <button
        ref={fabBtnRef}
        className="fab-new-chat"
        title={t('newChat.title')}
        onClick={() => {
          if (fabMenuOpen) { setFabMenuOpen(false); return; }
          const r = fabBtnRef.current?.getBoundingClientRect();
          if (r) setFabMenuPos(clampMenuPos(r.right - 190, r.top - 98, 190, 90));
          setFabMenuOpen(true);
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      </button>}
      {fabMenuOpen && fabMenuPos && createPortal(
        <>
          <div className="menu-backdrop" onClick={() => setFabMenuOpen(false)} />
          <div className="menu menu-fixed" style={{ left: fabMenuPos.x, top: fabMenuPos.y }}>
            <button className="menu-item" onClick={() => { setFabMenuOpen(false); setNewChatOpen(1); }}>
              {t('newChat.personal')}
            </button>
            <button className="menu-item" onClick={() => { setFabMenuOpen(false); setNewChatOpen(2); }}>
              {t('newChat.group')}
            </button>
          </div>
        </>,
        document.body,
      )}

      {chatMenu && (
        <>
          <div className="menu-backdrop" onClick={() => setChatMenu(null)} />
          <div className="chat-ctx-menu" style={{ left: chatMenu.x, top: chatMenu.y }}>
            <button className="menu-item" onClick={togglePin}>
              <img src="/pin.svg" alt="" /> {t(chatMenu.pinnedAt ? 'context.unpin' : 'context.pin')}
            </button>
            <button className="menu-item" onClick={toggleArchive}>
              <img src="/bookmark.svg" alt="" /> {t(chatMenu.archived ? 'context.unarchive' : 'context.archive')}
            </button>
            {!chatMenu.isSupport && !chatMenu.isNotes && (
              <button className="menu-item danger" onClick={askLeaveChat}>
                <img src="/trash.svg" alt="" /> {t(chatMenu.type === 2 ? 'context.leaveGroup' : 'context.deleteChat')}
              </button>
            )}
          </div>
        </>
      )}

      {sharedModals}
    </aside>
  );
}
